"use client";

import { useEffect, useRef, useState } from "react";
import {
  DoorClosed,
  Hotel,
  LayoutDashboard,
  LucideIcon,
  LogOut,
  Package,
  ShoppingCart,
  Star,
  Tags,
  User,
  User2,
  UserCog,
  UserPen,
  UserRound,
  UsersRound,
  CreditCard,
  FileText,
  Receipt,
  FileSpreadsheet,
  Coins,
  Landmark,
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Button } from "./button";
import { rolesTranslations } from "@/lib/utils/Offices";
import { cn } from "@/lib/utils";
import { userLinked } from "@/lib/utils/constans";
import { validateRoutes } from "@/lib/utils/validateRoutes";
import Link from "next/link";

const routeIconMap = {
  DoorClosed,
  Hotel,
  LayoutDashboard,
  Package,
  ShoppingCart,
  Star,
  Tags,
  User2,
  UserCog,
  UserPen,
  UserRound,
  UsersRound,
  CreditCard,
  FileText,
  Receipt,
  FileSpreadsheet,
  Coins,
  Landmark,
};

type SessionRouteLike = {
  label?: string;
  name?: string;
  path?: string;
  route?: string;
  icon?: keyof typeof routeIconMap;
};

type DropdownRoute = {
  label: string;
  path: string;
  Icon: LucideIcon;
};

const billingRoutes = [
  { label: "Facturas", path: "/usuario/facturacion/facturas", Icon: FileText },
  {
    label: "Facturas de anticipo",
    path: "/usuario/facturacion/facturas-anticipo",
    Icon: Coins,
  },
  { label: "Mis pagos", path: "/usuario/facturacion/pagos", Icon: CreditCard },
  {
    label: "Notas de crédito",
    path: "/usuario/facturacion/notas-de-credito",
    Icon: Receipt,
  },
  // { label: "Mis pagos", path: "/usuario/facturacion/pagos", Icon: CreditCard },
  // { label: "Facturas", path: "/usuario/facturacion/facturas", Icon: FileText },
  // { label: "Notas de crédito", path: "/usuario/facturacion/notas-de-credito", Icon: Receipt },
  // { label: "Facturas de anticipo", path: "/usuario/facturacion/facturas-anticipo", Icon: Coins },
];

export default function UserSection() {
  const { data: session } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const user = session?.user;
  const userRoles: string[] = [
    ...(user?.role ? [user.role] : []),
    ...(user?.tokenDecode?.roles || []),
  ];
  const displayRole =
    userRoles.find((r) => r === "ROLE_ADMIN") ||
    userRoles.find((r) => r === "ROLE_SUPPLIER") ||
    userRoles.find((r) => r === "ROLE_INTEGRATOR" || r === "ROLE_INTEGRADOR") ||
    user?.role ||
    userRoles[0] ||
    "";

  const isLinked = user?.tokenDecode?.accountType === userLinked;

  let displayRoleLabel = rolesTranslations[displayRole] || "Cliente";
  if (displayRole !== "ROLE_ADMIN" && displayRole !== "ROLE_SUPPLIER") {
    if (isLinked) {
      displayRoleLabel = "Integrador";
    }
  }
  const isCustomer =
    displayRole !== "ROLE_ADMIN" &&
    displayRole !== "ROLE_SUPPLIER" &&
    !userRoles.includes("ROLE_FINANCE");

  const optionStyles =
    "cursor-pointer hover:bg-gray-50 w-full px-4 py-3 flex items-center gap-3 transition-colors text-[12px] text-left";
  const sessionRoutes = (
    user ? validateRoutes(user) : ([] as SessionRouteLike[])
  )
    .map((route) => ({
      label: route.label || route.name,
      path: route.path || route.route,
      Icon: routeIconMap[route.icon as keyof typeof routeIconMap] || UserPen,
    }))
    .filter((route): route is DropdownRoute =>
      Boolean(route.label && route.path),
    );

  const accountRoutePaths = [
    "/usuario",
    "/usuario/datos-comerciales",
    "/usuario/perfil-fiscal",
    "/usuario/direcciones",
    "/usuario/marcas",
  ];

  const allowedBillingRoutes = billingRoutes.filter((br) =>
    sessionRoutes.some((sr) => sr.path === br.path),
  );

  const myAccountRoutes = sessionRoutes.filter((r) =>
    accountRoutePaths.includes(r.path),
  );

  const accessRoutes = sessionRoutes.filter(
    (r) =>
      !accountRoutePaths.includes(r.path) &&
      !billingRoutes.some((br) => br.path === r.path),
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function formatName(u?: { name?: string | null; email?: string | null }) {
    const rawName = u?.name?.trim() || u?.email?.trim() || "";
    if (!rawName) return "";
    const names = rawName.split(/\s+/).filter(Boolean);
    if (names.length === 0) return "";
    return names.length > 1
      ? `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase()
      : names[0].charAt(0).toUpperCase();
  }

  async function handleCloseSession() {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch (error) {
      console.error("Error al ejecutar el endpoint de logout:", error);
    } finally {
      setIsOpen(false);
      signOut({
        callbackUrl: "/iniciar-sesion",
        redirect: true,
      });
    }
  }

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <Button
        className="px-2 min-h-[40px] max-h-[40px] cursor-pointer flex items-center justify-center flex-col w-[40px] h-12 border border-gray-200 text-secondary rounded-full text-sm transition-all hover:shadow-md hover:!bg-white hover:!font-bold"
        onClick={() =>
          user ? setIsOpen(!isOpen) : router.push("/iniciar-sesion")
        }
        tooltip={user ? user.name || user.email || "Usuario" : "Invitado"}
        variant="ghost"
      >
        {user ? (
          <span className="font-bold">{formatName(user)}</span>
        ) : (
          <User size={18} />
        )}
      </Button>

      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 right-0 bg-white text-black rounded-lg shadow-2xl border border-gray-100 z-[100] overflow-hidden transition-all w-[calc(100vw-32px)] sm:w-[360px]",
            userRoles.includes("ROLE_ADMIN")
              ? "md:w-[240px] md:min-w-[240px]"
              : isCustomer && allowedBillingRoutes.length > 0
                ? "md:w-[700px] md:min-w-[700px]"
                : "md:w-[460px] md:min-w-[460px]",
          )}
        >
          <div className="px-4 py-3 border-b border-gray-100 bg-white">
            <p className="text-sm font-bold text-blue-600">{user?.name}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">
              {displayRoleLabel}
            </p>
          </div>

          {userRoles.includes("ROLE_ADMIN") ? (
            <div className="py-1">
              <Link
                href="/admin"
                onClick={() => setIsOpen(false)}
                className={optionStyles}
              >
                <LayoutDashboard size={16} />
                Panel de administración
              </Link>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:divide-x divide-y md:divide-y-0 divide-gray-100">
              {myAccountRoutes.length > 0 && (
                <div className="flex-1 py-1">
                  <p className="px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    Mi cuenta
                  </p>
                  {myAccountRoutes.map((route) => (
                    <Link
                      key={route.path}
                      href={route.path}
                      onClick={() => setIsOpen(false)}
                      className={optionStyles}
                    >
                      <route.Icon size={16} />
                      {route.label}
                    </Link>
                  ))}
                </div>
              )}

              {accessRoutes.length > 0 && (
                <div className="flex-1 py-1">
                  <p className="px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    Accesos
                  </p>
                  {accessRoutes.map((route) => (
                    <Link
                      key={route.path}
                      href={route.path}
                      onClick={() => setIsOpen(false)}
                      className={optionStyles}
                    >
                      <route.Icon size={16} />
                      {route.label}
                    </Link>
                  ))}
                </div>
              )}

              {isCustomer && allowedBillingRoutes.length > 0 && (
                <div className="flex-1 py-1">
                  <p className="px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    Facturación
                  </p>
                  {allowedBillingRoutes.map((route) => (
                    <Link
                      key={route.path}
                      href={route.path}
                      onClick={() => setIsOpen(false)}
                      className={optionStyles}
                    >
                      <route.Icon size={16} />
                      {route.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="border-t border-gray-100">
            <button
              onClick={handleCloseSession}
              className="cursor-pointer hover:bg-red-50 w-full px-4 py-3 flex items-center gap-3 text-red-600 transition-colors text-sm font-medium"
            >
              <LogOut size={16} />
              Cerrar sesion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
